/**
 * Training Page Help Content
 *
 * This file demonstrates the full enterprise help structure including:
 * - Tips/Alerts
 * - Step-by-step walkthroughs
 * - Video tutorials
 * - Troubleshooting guides
 * - FAQs with tags
 * - Role-based visibility
 * - Support contact info
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createWalkthrough,
  createVideo,
  createTroubleshooting,
  createRelatedLink,
} from '../utils';

export const trainingHelp: HelpContent = createHelpContent({
  id: 'training',
  title: 'Training & Development',
  description: 'Learning resources, courses, and professional development',
  lastUpdated: '2026-02-04',
  version: '2.0',

  // ─────────────────────────────────────────────────────────────────────────
  // TIPS - Shown at the top of the help panel
  // ─────────────────────────────────────────────────────────────────────────
  tips: [
    createTip(
      'tip-keyboard',
      'pro-tip',
      'Keyboard Shortcut',
      'Press "?" anywhere to open this help panel. Press ESC to close.'
    ),
    createTip(
      'tip-mobile',
      'info',
      'Mobile Learning',
      'All courses are mobile-friendly. Learn on the go from your phone or tablet.'
    ),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // SECTIONS - Main content organized by topic
  // ─────────────────────────────────────────────────────────────────────────
  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Training section is your learning hub for professional development. Access courses, track your progress, and build new skills aligned with your career goals and organizational needs.</p>
      <p>The training system includes:</p>
      <ul>
        <li><strong>My Learning</strong> - Your enrolled courses and progress</li>
        <li><strong>Course Library</strong> - Browse all available training</li>
        <li><strong>Learning Paths</strong> - Curated course sequences</li>
        <li><strong>Personal Development Plan</strong> - Goal setting with your manager</li>
      </ul>`
    ),
    createSection(
      'my-learning',
      'My Learning Tab',
      `<p>The "My Learning" tab shows courses you're currently enrolled in and your progress. Features include:</p>
      <ul>
        <li><strong>In-Progress Courses</strong> - Resume where you left off</li>
        <li><strong>Completed Courses</strong> - View your achievements and certificates</li>
        <li><strong>Progress Tracking</strong> - See completion percentages and time spent</li>
        <li><strong>Due Dates</strong> - Mandatory training deadlines highlighted</li>
      </ul>
      <p>Click on any course card to continue learning or view details.</p>`
    ),
    createSection(
      'library',
      'Course Library',
      `<p>Browse the full catalog of available training modules in the Library tab. Courses are organized by:</p>
      <ul>
        <li><strong>Category</strong> - Technical, soft skills, compliance, leadership</li>
        <li><strong>Skill Level</strong> - Beginner, intermediate, advanced</li>
        <li><strong>Duration</strong> - Estimated time to complete</li>
        <li><strong>Format</strong> - Video, interactive, reading, assessment</li>
      </ul>
      <p>Use the filters and search to find relevant courses. Click "Enroll" to start any course.</p>`
    ),
    createSection(
      'learning-paths',
      'Learning Paths',
      `<p>Learning paths are curated sequences of courses designed to help you master a skill or role. Benefits include:</p>
      <ul>
        <li>Structured progression from fundamentals to advanced topics</li>
        <li>Courses that build on each other</li>
        <li>Clear milestones and achievements</li>
        <li>Recommended by your organization for specific career tracks</li>
      </ul>
      <p>Enrolling in a learning path automatically adds all courses to your queue in the recommended order.</p>`
    ),
    createSection(
      'pdp',
      'Personal Development Plan (PDP)',
      `<p>Your PDP helps you set and track development goals. Work with your manager to:</p>
      <ul>
        <li>Define career objectives</li>
        <li>Identify skill gaps</li>
        <li>Plan training activities</li>
        <li>Track progress toward goals</li>
        <li>Document achievements and growth</li>
      </ul>
      <p>PDPs are reviewed quarterly. Your manager can recommend specific courses based on your goals.</p>`,
    ),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // WALKTHROUGHS - Step-by-step guides
  // ─────────────────────────────────────────────────────────────────────────
  walkthroughs: [
    createWalkthrough(
      'walkthrough-enroll',
      'How to Enroll in a Course',
      'Learn how to find and enroll in training courses.',
      [
        {
          title: 'Go to the Library tab',
          description: 'Click on the "Library" tab to browse available courses.',
        },
        {
          title: 'Find a course',
          description: 'Use search or filters to find a course that interests you.',
        },
        {
          title: 'View course details',
          description: 'Click on the course card to see the full description, duration, and prerequisites.',
        },
        {
          title: 'Click Enroll',
          description: 'Click the "Enroll" or "Start Course" button to add it to your learning.',
        },
        {
          title: 'Begin learning',
          description: 'The course will appear in your "My Learning" tab. Click to start!',
        },
      ],
      3
    ),
    createWalkthrough(
      'walkthrough-certificate',
      'How to Download Your Certificate',
      'Get your completion certificate after finishing a course.',
      [
        {
          title: 'Complete all modules',
          description: 'Finish all lessons and pass any required assessments.',
        },
        {
          title: 'Go to My Learning',
          description: 'Navigate to the "My Learning" tab and find the completed course.',
        },
        {
          title: 'Click on the course',
          description: 'Open the course details page.',
        },
        {
          title: 'Download certificate',
          description: 'Click the "Download Certificate" button. You can also share it to LinkedIn.',
        },
      ],
      2
    ),
    createWalkthrough(
      'walkthrough-path',
      'How to Start a Learning Path',
      'Enroll in a structured learning path for comprehensive skill development.',
      [
        {
          title: 'Go to Learning Paths',
          description: 'Click on the "Learning Paths" tab to see available paths.',
        },
        {
          title: 'Browse paths',
          description: 'Review paths relevant to your role or career goals.',
        },
        {
          title: 'View path details',
          description: 'Click a path to see included courses, duration, and outcomes.',
        },
        {
          title: 'Enroll in path',
          description: 'Click "Start Path" to enroll in all courses in the recommended sequence.',
        },
        {
          title: 'Track progress',
          description: 'Monitor your path progress from the My Learning tab.',
        },
      ],
      5
    ),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // VIDEOS - Tutorial videos (URLs are placeholders)
  // ─────────────────────────────────────────────────────────────────────────
  videos: [
    createVideo(
      'video-overview',
      'Training Platform Overview',
      'A quick tour of the training platform and its features.',
      'https://example.com/videos/training-overview',
      { durationSeconds: 180 }
    ),
    createVideo(
      'video-paths',
      'Understanding Learning Paths',
      'Learn how learning paths can accelerate your development.',
      'https://example.com/videos/learning-paths',
      { durationSeconds: 240 }
    ),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // TROUBLESHOOTING - Common issues and solutions
  // ─────────────────────────────────────────────────────────────────────────
  troubleshooting: [
    createTroubleshooting(
      'trouble-video',
      'Video not playing or loading slowly',
      [
        'Slow internet connection',
        'Browser cache issues',
        'Browser not supported',
        'Company firewall blocking video',
      ],
      [
        {
          description: 'Try refreshing the page',
          steps: ['Press F5 or click the refresh button', 'Wait for the page to fully reload'],
        },
        {
          description: 'Clear browser cache',
          steps: [
            'Open browser settings',
            'Find "Clear browsing data"',
            'Select "Cached images and files"',
            'Click Clear',
          ],
        },
        {
          description: 'Use a different browser',
          steps: ['Try Chrome, Firefox, or Edge', 'Ensure your browser is updated'],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-progress',
      'Course progress not saving',
      [
        'Session timed out',
        'Browser blocking cookies',
        'Network interruption',
      ],
      [
        {
          description: 'Check your connection and refresh',
          steps: [
            'Ensure you have a stable internet connection',
            'Refresh the page',
            'Try completing the section again',
          ],
        },
        {
          description: 'Enable cookies for this site',
          steps: [
            'Check browser settings for cookie permissions',
            'Add this site to allowed cookies list',
          ],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-certificate',
      'Certificate not generating after completion',
      [
        'Assessment not passed',
        'Not all modules completed',
        'System processing delay',
      ],
      [
        {
          description: 'Verify all requirements are met',
          steps: [
            'Check that all modules show 100% completion',
            'Verify you passed any required assessments',
            'Check the minimum passing score requirement',
          ],
        },
        {
          description: 'Wait and check again',
          steps: [
            'Certificates may take a few minutes to generate',
            'Refresh the page after 5 minutes',
            'Check your email for certificate notification',
          ],
        },
      ],
      'mailto:hr@company.com?subject=Certificate%20Issue'
    ),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // FAQs - Frequently asked questions
  // ─────────────────────────────────────────────────────────────────────────
  faqs: [
    createFAQ(
      'faq-enroll',
      'How do I enroll in a course?',
      'Navigate to the Library tab, find a course you\'re interested in, and click the "Enroll" or "Start Course" button. The course will then appear in your "My Learning" section.',
      ['enroll', 'start', 'course', 'register', 'begin']
    ),
    createFAQ(
      'faq-mobile',
      'Can I take courses on my mobile device?',
      'Yes, the training platform is fully responsive and mobile-friendly. You can access courses, watch videos, and complete assessments on your phone or tablet. Some interactive content works best on larger screens.',
      ['mobile', 'phone', 'tablet', 'device', 'responsive']
    ),
    createFAQ(
      'faq-certificate',
      'Do I get a certificate when I complete a course?',
      'Most courses award a certificate of completion. Certificates are stored in your profile and can be downloaded as PDF or shared directly to LinkedIn. Some courses may require passing an assessment to earn the certificate.',
      ['certificate', 'completion', 'credential', 'award', 'linkedin']
    ),
    createFAQ(
      'faq-progress',
      'What if I don\'t finish a course in one session?',
      'Your progress is automatically saved. When you return to the course, you can resume from exactly where you left off. Look for the "Continue" button on your course card.',
      ['progress', 'save', 'resume', 'continue', 'session']
    ),
    createFAQ(
      'faq-request',
      'How do I request new training content?',
      'Contact your manager or HR to suggest new training topics. You can also use the feedback button within the training section to submit content requests. We regularly review requests and update our catalog.',
      ['request', 'suggest', 'new', 'content', 'feedback']
    ),
    createFAQ(
      'faq-mandatory',
      'Are there mandatory courses I need to complete?',
      'Yes, some courses are marked as mandatory for compliance, security, or onboarding purposes. These appear highlighted in your "My Learning" section with a due date. You\'ll receive reminders as deadlines approach.',
      ['mandatory', 'required', 'compliance', 'deadline', 'onboarding']
    ),
    createFAQ(
      'faq-manager',
      'Can my manager see my training progress?',
      'Yes, managers can view training progress for their team members. This helps with development planning and ensures mandatory training is completed. Detailed quiz scores are typically private.',
      ['manager', 'visibility', 'privacy', 'team', 'view'],
      { visibility: { visibleTo: ['staff', 'all'] } }
    ),
    createFAQ(
      'faq-assign',
      'How do I assign training to my team?',
      'As a manager, go to your Team Dashboard and select "Assign Training." You can assign individual courses or learning paths, set due dates, and track completion. Team members will receive email notifications.',
      ['assign', 'team', 'manager', 'delegate'],
      { visibility: { visibleTo: ['manager', 'team_lead', 'admin'] } }
    ),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // RELATED LINKS
  // ─────────────────────────────────────────────────────────────────────────
  relatedLinks: [
    createRelatedLink('Dashboard', '/', { description: 'View your training summary on the dashboard' }),
    createRelatedLink('Knowledge Base', '/knowledge', { description: 'Find supplementary learning materials' }),
    createRelatedLink('Directory', '/directory', { description: 'Connect with mentors and subject matter experts' }),
    createRelatedLink('HR Policies', '/knowledge?category=hr', { description: 'Training and development policies' }),
  ],

  // ─────────────────────────────────────────────────────────────────────────
  // SUPPORT CONTACT
  // ─────────────────────────────────────────────────────────────────────────
  supportContact: {
    name: 'Learning & Development Team',
    email: 'learning@company.com',
    slackChannel: '#help-training',
  },

  // ─────────────────────────────────────────────────────────────────────────
  // KEYWORDS for search
  // ─────────────────────────────────────────────────────────────────────────
  keywords: [
    'training',
    'courses',
    'learning',
    'development',
    'skills',
    'education',
    'modules',
    'certification',
    'professional development',
    'e-learning',
    'pdp',
    'career growth',
  ],
});
